import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
// Create payment thunk
export const create_payment = createAsyncThunk('payments/create_payment', async (payment_data, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/payments', payment_data);
        return response.data.data || response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to create payment');
    }
});
// Create subscription thunk
export const create_subscription = createAsyncThunk('payments/create_subscription', async (subscription_data, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/payments/subscription', {
            amount: subscription_data.amount,
            tutorId: subscription_data.tutor_id,
            recurrence: subscription_data.recurrence,
            paymentMethod: subscription_data.payment_method,
            transactionId: subscription_data.transaction_id,
            sessionsPerPeriod: subscription_data.sessions_per_period || 4,
            metadata: subscription_data.metadata
        });
        return response.data.data || response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to create subscription');
    }
});
// Fetch student subscriptions
export const fetch_student_subscriptions = createAsyncThunk('payments/fetch_student_subscriptions', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/payments/subscriptions/student');
        return response.data.data || response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch subscriptions');
    }
});
// Fetch tutor subscriptions
export const fetch_tutor_subscriptions = createAsyncThunk('payments/fetch_tutor_subscriptions', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/payments/subscriptions/tutor');
        return response.data.data || response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch subscriptions');
    }
});
// Cancel subscription
export const cancel_subscription = createAsyncThunk('payments/cancel_subscription', async ({ subscription_id, cancel_reason }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/payments/subscription/${subscription_id}/cancel`, cancel_reason ? { cancelReason: cancel_reason } : {});
        return response.data.data || response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to cancel subscription');
    }
});
// Fetch tutor payment analytics
export const fetch_tutor_payment_analytics = createAsyncThunk('payments/fetch_tutor_analytics', async ({ tutor_id, period = 'month', classroom_id, student_id, start_date, end_date }, { rejectWithValue }) => {
    try {
        let url = `/payments/tutor/analytics?period=${period}`;
        if (classroom_id) {
            url += `&classroomId=${classroom_id}`;
        }
        if (student_id) {
            url += `&studentId=${student_id}`;
        }
        if (start_date) {
            url += `&startDate=${start_date}`;
        }
        if (end_date) {
            url += `&endDate=${end_date}`;
        }
        const response = await axiosInstance.get(url);
        return response.data.data || response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch tutor payment analytics');
    }
});
// Fetch student payment analytics
export const fetch_student_payment_analytics = createAsyncThunk('payments/fetch_student_analytics', async ({ student_id, period = 'month', tutor_id }, { rejectWithValue }) => {
    try {
        let url = `/payments/student/analytics?period=${period}`;
        if (tutor_id) {
            url += `&tutorId=${tutor_id}`;
        }
        const response = await axiosInstance.get(url);
        return response.data.data || response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch student payment analytics');
    }
});
// Check subscription status thunk
export const checkSubscriptionStatus = createAsyncThunk('payments/checkSubscriptionStatus', async ({ userId, userRole }, { rejectWithValue }) => {
    try {
        let endpoint = '';
        if (userRole === 'tutor') {
            endpoint = `/tutor/subscription/status/${userId}`;
        }
        else {
            endpoint = `/subscriptions/current`;
        }
        const response = await axiosInstance.get(endpoint);
        const responseData = response.data;
        // Handle different response formats
        // Tutor endpoint: { success: true, data: subscription || null }
        // General endpoint: { success: true, data: subscription } or 404
        const subscription = responseData.data || responseData.subscription || responseData;
        // If no subscription found
        if (!subscription || (responseData.success === false)) {
            return {
                hasActiveSubscription: false,
                subscription: null,
                status: 'none',
                expiresAt: null,
                nextBillingDate: null,
                paymentFailed: false
            };
        }
        const now = new Date();
        const endDate = subscription.subscription_end_date ? new Date(subscription.subscription_end_date) : null;
        // Determine status based on payment status and dates
        let status = 'active';
        // Check payment status first
        if (subscription.status === 'canceled' || subscription.status === 'cancelled') {
            status = 'canceled';
        }
        else if (subscription.status === 'pending') {
            status = 'pending';
        }
        else if (subscription.status === 'failed') {
            status = 'expired'; // Treat failed as expired
        }
        else if (subscription.status === 'completed') {
            // Check if subscription has expired based on end date
            if (endDate && endDate < now) {
                status = 'expired';
            }
            else {
                status = 'active';
            }
        }
        else {
            // Default to active if status is unclear but subscription exists
            if (endDate && endDate < now) {
                status = 'expired';
            }
            else {
                status = 'active';
            }
        }
        const paymentFailed = !!subscription?.metadata?.last_payment_failure;
        return {
            hasActiveSubscription: status === 'active',
            subscription: subscription,
            status,
            expiresAt: subscription.subscription_end_date || null,
            nextBillingDate: subscription.next_billing_date || null,
            paymentFailed
        };
    }
    catch (error) {
        // If 404 or no subscription, return 'none' status
        if (error.response?.status === 404 || error.response?.status === 400) {
            return {
                hasActiveSubscription: false,
                subscription: null,
                status: 'none',
                expiresAt: null,
                nextBillingDate: null,
                paymentFailed: false
            };
        }
        return rejectWithValue(error.message || 'Failed to check subscription status');
    }
});
// Get payment by ID thunk
export const get_payment_by_id = createAsyncThunk('payments/get_payment_by_id', async (payment_id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/payments/${payment_id}`);
        return response.data.data || response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch payment');
    }
});
// Update payment status thunk
export const update_payment_status = createAsyncThunk('payments/update_payment_status', async ({ payment_id, status }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(`/payments/${payment_id}/status`, { status });
        return response.data.data || response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to update payment status');
    }
});
// Create Stripe payment intent thunk
export const create_stripe_payment_intent = createAsyncThunk('payments/create_stripe_payment_intent', async ({ amount, currency = 'USD', couponCode }, { rejectWithValue }) => {
    try {
        const payload = { amount, currency };
        if (couponCode?.trim()) {
            payload.couponCode = couponCode.trim();
        }
        const response = await axiosInstance.post('/payments/stripe/intent', payload);
        return response.data.data || response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to create Stripe payment intent');
    }
});
// Cancel payment thunk
export const cancel_payment = createAsyncThunk('payments/cancel_payment', async ({ payment_id }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(`/payments/${payment_id}/status`, { status: 'canceled' });
        return response.data.data || response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to cancel payment');
    }
});
// Initial state
const initial_state = {
    payments: [],
    subscriptions: [],
    tutor_analytics: null,
    student_analytics: null,
    current_payment: null,
    subscriptionStatus: null,
    loading: false,
    error: null,
    success: false,
};
// Create payment slice
const payment_slice = createSlice({
    name: 'payments',
    initialState: initial_state,
    reducers: {
        reset_payment_state: (state) => {
            state.error = null;
            state.success = false;
            state.loading = false;
        },
        clear_current_payment: (state) => {
            state.current_payment = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Create Payment
            .addCase(create_payment.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(create_payment.fulfilled, (state, action) => {
            state.loading = false;
            state.success = true;
            state.payments.push(action.payload);
            state.current_payment = action.payload;
            // If it's a subscription, add to subscriptions array too
            if (action.payload.is_subscription) {
                state.subscriptions.push(action.payload);
            }
        })
            .addCase(create_payment.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
        })
            // Create Subscription
            .addCase(create_subscription.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(create_subscription.fulfilled, (state, action) => {
            state.loading = false;
            state.success = true;
            state.subscriptions.push(action.payload);
            state.payments.push(action.payload);
            state.current_payment = action.payload;
            // Update subscription status when subscription is created
            const now = new Date();
            const endDate = action.payload.subscription_end_date ? new Date(action.payload.subscription_end_date) : null;
            let status = 'active';
            if (action.payload.status === 'canceled') {
                status = 'canceled';
            }
            else if (action.payload.status === 'pending') {
                status = 'pending';
            }
            else if (action.payload.status === 'failed') {
                status = 'expired';
            }
            else if (action.payload.status === 'completed') {
                if (endDate && endDate < now) {
                    status = 'expired';
                }
                else {
                    status = 'active';
                }
            }
            else {
                if (endDate && endDate < now) {
                    status = 'expired';
                }
                else {
                    status = 'active';
                }
            }
            state.subscriptionStatus = {
                hasActiveSubscription: status === 'active',
                subscription: action.payload,
                status,
                expiresAt: action.payload.subscription_end_date || null,
                nextBillingDate: action.payload.next_billing_date || null
            };
        })
            .addCase(create_subscription.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
        })
            // Fetch Student Subscriptions
            .addCase(fetch_student_subscriptions.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetch_student_subscriptions.fulfilled, (state, action) => {
            state.loading = false;
            state.subscriptions = action.payload;
        })
            .addCase(fetch_student_subscriptions.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
        })
            // Fetch Tutor Subscriptions
            .addCase(fetch_tutor_subscriptions.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetch_tutor_subscriptions.fulfilled, (state, action) => {
            state.loading = false;
            state.subscriptions = action.payload;
        })
            .addCase(fetch_tutor_subscriptions.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
        })
            // Cancel Subscription
            .addCase(cancel_subscription.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(cancel_subscription.fulfilled, (state, action) => {
            state.loading = false;
            state.success = true;
            // Update the subscription in both arrays
            const updatedSubscription = action.payload;
            // Update in subscriptions array
            const subIndex = state.subscriptions.findIndex(s => s.id === updatedSubscription.id);
            if (subIndex !== -1) {
                state.subscriptions[subIndex] = updatedSubscription;
            }
            // Update in payments array
            const payIndex = state.payments.findIndex(p => p.id === updatedSubscription.id);
            if (payIndex !== -1) {
                state.payments[payIndex] = updatedSubscription;
            }
            if (state.current_payment?.id === updatedSubscription.id) {
                state.current_payment = updatedSubscription;
            }
        })
            .addCase(cancel_subscription.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
        })
            // Get Payment by ID
            .addCase(get_payment_by_id.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(get_payment_by_id.fulfilled, (state, action) => {
            state.loading = false;
            state.current_payment = action.payload;
        })
            .addCase(get_payment_by_id.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
        })
            // Check Subscription Status
            .addCase(checkSubscriptionStatus.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(checkSubscriptionStatus.fulfilled, (state, action) => {
            state.loading = false;
            state.subscriptionStatus = action.payload;
            state.error = null;
        })
            .addCase(checkSubscriptionStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'Failed to check subscription status';
            // Set default status on error
            state.subscriptionStatus = {
                hasActiveSubscription: false,
                subscription: null,
                status: 'none',
                expiresAt: null,
                nextBillingDate: null,
                paymentFailed: false
            };
        })
            // Update Payment Status
            .addCase(update_payment_status.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(update_payment_status.fulfilled, (state, action) => {
            state.loading = false;
            state.success = true;
            state.current_payment = action.payload;
            const index = state.payments.findIndex(p => p.id === action.payload.id);
            if (index !== -1) {
                state.payments[index] = action.payload;
            }
            // Update subscription if this is a subscription
            if (action.payload.is_subscription) {
                const subIndex = state.subscriptions.findIndex(s => s.id === action.payload.id);
                if (subIndex !== -1) {
                    state.subscriptions[subIndex] = action.payload;
                }
                else {
                    // If subscription doesn't exist in array, add it
                    state.subscriptions.push(action.payload);
                }
                // Update subscription status when payment status is updated
                const now = new Date();
                const endDate = action.payload.subscription_end_date ? new Date(action.payload.subscription_end_date) : null;
                let status = 'active';
                if (action.payload.status === 'canceled') {
                    status = 'canceled';
                }
                else if (action.payload.status === 'pending') {
                    status = 'pending';
                }
                else if (action.payload.status === 'failed') {
                    status = 'expired';
                }
                else if (action.payload.status === 'completed') {
                    if (endDate && endDate < now) {
                        status = 'expired';
                    }
                    else {
                        status = 'active';
                    }
                }
                else {
                    if (endDate && endDate < now) {
                        status = 'expired';
                    }
                    else {
                        status = 'active';
                    }
                }
                state.subscriptionStatus = {
                    hasActiveSubscription: status === 'active',
                    subscription: action.payload,
                    status,
                    expiresAt: action.payload.subscription_end_date || null,
                    nextBillingDate: action.payload.next_billing_date || null
                };
            }
        })
            .addCase(update_payment_status.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
        })
            // Fetch Tutor Payment Analytics
            .addCase(fetch_tutor_payment_analytics.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetch_tutor_payment_analytics.fulfilled, (state, action) => {
            state.loading = false;
            state.tutor_analytics = action.payload;
        })
            .addCase(fetch_tutor_payment_analytics.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
        })
            // Fetch Student Payment Analytics
            .addCase(fetch_student_payment_analytics.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetch_student_payment_analytics.fulfilled, (state, action) => {
            state.loading = false;
            state.student_analytics = action.payload;
        })
            .addCase(fetch_student_payment_analytics.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
        })
            // Create Stripe Payment Intent
            .addCase(create_stripe_payment_intent.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(create_stripe_payment_intent.fulfilled, (state) => {
            state.loading = false;
            state.success = true;
        })
            .addCase(create_stripe_payment_intent.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
        })
            // Cancel Payment
            .addCase(cancel_payment.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(cancel_payment.fulfilled, (state, action) => {
            state.loading = false;
            state.success = true;
            state.current_payment = action.payload;
            const index = state.payments.findIndex(p => p.id === action.payload.id);
            if (index !== -1) {
                state.payments[index] = action.payload;
            }
            // Update subscription if this is a subscription
            if (action.payload.is_subscription) {
                const subIndex = state.subscriptions.findIndex(s => s.id === action.payload.id);
                if (subIndex !== -1) {
                    state.subscriptions[subIndex] = action.payload;
                }
            }
        })
            .addCase(cancel_payment.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
        });
    },
});
export const { reset_payment_state, clear_current_payment } = payment_slice.actions;
// Selectors
export const selectSubscriptionStatus = (state) => state.payments.subscriptionStatus;
export const selectHasActiveSubscription = (state) => state.payments.subscriptionStatus?.hasActiveSubscription ?? false;
export const selectSubscriptionLoading = (state) => state.payments.loading;
export const selectSubscriptionError = (state) => state.payments.error;
export default payment_slice.reducer;
